import {
  Company,
  CompaniesSearchRequest,
  CompaniesSearchResponse,
  COMPANIES_SEARCH_MAX_PAGE_SIZE,
} from 'models'
import { z } from 'zod'
import { nonNegativeIntSchema, parseWithSchema, positiveIntSchema } from './zodValidation'

const companiesSearchItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    commercialName: z.string(),
    commercialNameInt: z.string(),
    parentId: z.string(),
    status: z.boolean(),
    roles: nonNegativeIntSchema,
    children: z.array(z.string()).nullable(),
    childrenCount: nonNegativeIntSchema.nullable(),
  })
  .passthrough()

const companiesSearchResponseSchema = z
  .object({
    items: z.array(companiesSearchItemSchema),
    page: positiveIntSchema,
    pageSize: positiveIntSchema,
    totalCount: nonNegativeIntSchema,
  })
  .passthrough()

const sanitizeCompaniesRequest = (request?: CompaniesSearchRequest): CompaniesSearchRequest => {
  const normalizedRequest =
    request && typeof request === 'object' ? { ...request } : ({} as CompaniesSearchRequest)

  const rawPage = Number(normalizedRequest.paging?.page)
  const rawPageSize = Number(normalizedRequest.paging?.pageSize)
  const page = Number.isFinite(rawPage) && rawPage > 0 ? Math.floor(rawPage) : 1
  const pageSizeCandidate =
    Number.isFinite(rawPageSize) && rawPageSize > 0
      ? Math.floor(rawPageSize)
      : COMPANIES_SEARCH_MAX_PAGE_SIZE

  normalizedRequest.paging = {
    page,
    pageSize: Math.min(pageSizeCandidate, COMPANIES_SEARCH_MAX_PAGE_SIZE),
  }

  return normalizedRequest
}

const parseCompaniesSearchResponse = (payload: unknown): CompaniesSearchResponse => {
  const parsed = parseWithSchema(companiesSearchResponseSchema, payload, 'companies/search')

  return {
    ...parsed,
    items: parsed.items as Array<Company>,
  }
}

export { sanitizeCompaniesRequest, parseCompaniesSearchResponse }
